import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    IconButton,
    Badge,
    Popover,
    Box,
    Typography,
    CircularProgress,
    Tooltip
} from '@mui/material';
import { useGetStatusWiseWeightDiscrepancyQuery, useGenericPostRequestQuery } from '../redux/commonRTK';
import { findWalletBalance } from '../helpers/UtilityHelper';
import { apiConstants } from '../common/constants';
import MonitorWeightOutlinedIcon from '@mui/icons-material/MonitorWeightOutlined';
import {FONT, SIZE, WEIGHT} from '../styles/foundation/typography';
import {NAVY, SUBTLE, BORDER, WHITE, PRIMARY_BLUE, WARNING} from '../styles/foundation/colors';
import {SHADOW} from '../styles/foundation/effects';

const WeightDiscrepancyAlert = ({ merchantId }) => {
    const navigate = useNavigate();
    const [anchorEl, setAnchorEl] = useState(null);

    const payload = {
        collection: "weight_discrepancy",
        andfilter: [
            { field: "merchantId", operator: "eq", value: merchantId },
            {
                "field": "status",
                "operator": "in",
                "value": [
                    "NEW",
                ]
            }
        ],
    };
    const { data: weightDiscrepancyData, isLoading: isLoadingDiscrepancy } = useGetStatusWiseWeightDiscrepancyQuery(
        payload,
    );

    const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const open = Boolean(anchorEl);

    const getStatusCount = (status) => {
        if (!weightDiscrepancyData?.[0]?.StatusCounts) return 0;
        const statusData = weightDiscrepancyData[0].StatusCounts.find(item => item._id === status);
        return statusData ? statusData.count : 0;
    };

    const newDiscrepancyCount = getStatusCount('NEW');

    // Money held against open discrepancies, shown here rather than in the wallet bar. Same request as
    // WalletDetails, so RTK Query serves both from one call.
    const { data: wallets } = useGenericPostRequestQuery({
        type: 'GET',
        url: `${apiConstants.GET_ALL_WALLET}`,
        data: { merchantId },
    });
    const heldAmount = Number((wallets ? findWalletBalance(wallets) : {}).heldAmount || 0);
    const inr = (v) => Intl.NumberFormat('en-IN', {style: 'currency', currency: 'INR'}).format(v || 0);
    // Held money can sit on a disputed case with no NEW one left, so it keeps the icon up too.
    const showAlert = newDiscrepancyCount > 0 || heldAmount > 0;

    return (
        <>
            {showAlert && (
                <Tooltip title={`Weight discrepancies (${newDiscrepancyCount})${heldAmount > 0 ? ` · ${inr(heldAmount)} on hold` : ''}`}>
                    <IconButton
                        onClick={handleClick}
                        size="small"
                        sx={{
                            color: 'text.secondary',
                            borderRadius: 1.5,
                            '&:hover': {bgcolor: 'action.hover', color: 'text.primary'},
                        }}
                    >
                        <Badge badgeContent={newDiscrepancyCount} color="error"
                            sx={{
                                '& .MuiBadge-badge': {
                                    fontSize: '0.6rem',
                                    height: 16,
                                    minWidth: 16,
                                    padding: '0 4px',
                                },
                            }}
                        >
                            <MonitorWeightOutlinedIcon sx={{fontSize: 20}} />
                        </Badge>
                    </IconButton>
                </Tooltip>
            )}

            <Popover
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'center',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'center',
                }}
                PaperProps={{
                    sx: {
                        width: heldAmount > 0 ? 380 : 320,
                        borderRadius: '12px',
                        overflow: 'hidden',
                        mt: 1,
                        border: `1px solid ${BORDER}`,
                        boxShadow: SHADOW.cardHover,
                    }
                }}
            >
                {/* Header — navy, consistent with the app + What's New popover */}
                <Box sx={{background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)', color: WHITE, p: 2, display: 'flex', alignItems: 'center', gap: 1.5}}>
                    <Box sx={{width: 34, height: 34, borderRadius: '8px', bgcolor: 'rgba(255,255,255,0.16)', display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                        <MonitorWeightOutlinedIcon sx={{fontSize: 20, color: WHITE}} />
                    </Box>
                    <Box>
                        <Typography sx={{fontFamily: FONT, fontSize: SIZE.sm, fontWeight: WEIGHT.bold, lineHeight: 1.2}}>Weight Discrepancies</Typography>
                        <Typography sx={{fontFamily: FONT, fontSize: SIZE['2xs'], color: 'rgba(255,255,255,0.85)'}}>New cases awaiting review</Typography>
                    </Box>
                </Box>

                {isLoadingDiscrepancy ? (
                    <Box sx={{display: 'flex', justifyContent: 'center', p: 3}}>
                        <CircularProgress size={24} sx={{color: PRIMARY_BLUE}} />
                    </Box>
                ) : showAlert ? (
                    <Box sx={{p: 2}}>
                        <Box sx={{display: 'flex', gap: 1, mb: 1.5}}>
                            <Box sx={{flex: 1, bgcolor: '#fafbfc', border: `1px solid ${BORDER}`, borderRadius: '8px', p: 1.25, textAlign: 'center'}}>
                                <Typography sx={{fontFamily: FONT, fontSize: SIZE.lg, fontWeight: WEIGHT.extra, color: WARNING, lineHeight: 1.2}}>{newDiscrepancyCount || 0}</Typography>
                                <Typography sx={{fontFamily: FONT, fontSize: SIZE['2xs'], color: SUBTLE, mt: 0.25}}>New Discrepancies</Typography>
                            </Box>
                            <Box sx={{flex: 1, bgcolor: '#fafbfc', border: `1px solid ${BORDER}`, borderRadius: '8px', p: 1.25, textAlign: 'center'}}>
                                <Typography sx={{fontFamily: FONT, fontSize: SIZE.md, fontWeight: WEIGHT.extra, color: NAVY, lineHeight: 1.2}}>{inr(Number(weightDiscrepancyData?.[0]?.["Monetary Impact"] || 0))}</Typography>
                                {/* 28 Oct: the excess on the NEW cases counted beside it — not the wallet's hold, which also covers disputed cases */}
                                <Typography sx={{fontFamily: FONT, fontSize: SIZE['2xs'], color: SUBTLE, mt: 0.25}}>Excess on new cases</Typography>
                            </Box>
                            {heldAmount > 0 && (
                                <Box sx={{flex: 1, bgcolor: '#fafbfc', border: `1px solid ${BORDER}`, borderRadius: '8px', p: 1.25, textAlign: 'center'}}>
                                    <Typography sx={{fontFamily: FONT, fontSize: SIZE.md, fontWeight: WEIGHT.extra, color: NAVY, lineHeight: 1.2}}>{inr(heldAmount)}</Typography>
                                    <Typography sx={{fontFamily: FONT, fontSize: SIZE['2xs'], color: SUBTLE, mt: 0.25}}>Held in wallet</Typography>
                                </Box>
                            )}
                        </Box>
                        <Box
                            onClick={() => {handleClose(); navigate('/weight-discrepancy');}}
                            sx={{display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5, cursor: 'pointer', color: PRIMARY_BLUE, '&:hover': {opacity: 0.8}}}
                        >
                            <Typography sx={{fontFamily: FONT, fontSize: SIZE.xs, fontWeight: WEIGHT.semibold}}>View all discrepancies →</Typography>
                        </Box>
                    </Box>
                ) : (
                    <Box sx={{p: 3, textAlign: 'center'}}>
                        <Typography sx={{fontFamily: FONT, fontSize: SIZE.xs, color: SUBTLE}}>No discrepancy data available</Typography>
                    </Box>
                )}
            </Popover>
        </>
    );
};

export default WeightDiscrepancyAlert;
